import { cn } from "@/helpers/utils";
import { cva, type VariantProps } from "cva";
import { Children, HTMLAttributes, PropsWithChildren, ReactNode } from "react";

const badgeVariants = cva("inline-flex items-center rounded-md text-xs transition-colors", {
    variants: {
        variant: {
            green: "bg-light-green text-accent",
            neutral: "bg-gray-100 text-accent",
            purple: "bg-purple-100 text-purple-400",
            blue: "bg-badge-blue text-accent",
            yellow: "bg-badge-yellow text-accent",
            red: "bg-badge-red text-accent",
        },
    },
    defaultVariants: {
        variant: "green",
    },
});

type BaseBadgeProps = PropsWithChildren<
    HTMLAttributes<HTMLDivElement> & {
        image?: ReactNode;
        icon?: ReactNode;
    } & VariantProps<typeof badgeVariants>
>;

export default function BaseBadge({
    children,
    className,
    image,
    icon,
    variant,
    ...divProps
}: BaseBadgeProps) {
    if (image && icon) {
        throw new Error("BaseBadge accepts either an image or an icon, not both.");
    }

    const hasContent = Children.count(children) > 0;
    const isIconOnly = !!icon && !hasContent;

    return (
        <div
            className={badgeVariants({
                variant,
                className: cn(
                    isIconOnly ? "h-[24px] w-[24px] justify-center p-0" : "px-[16px]",
                    className
                ),
            })}
            {...divProps}
        >
            {!!image && (
                <div
                    className={cn(
                        "flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[5px] bg-white",
                        hasContent && "mr-[8px]"
                    )}
                >
                    {image}
                </div>
            )}
            {!!icon && (
                <div
                    className={cn(
                        "inline-flex h-[16px] w-[16px] shrink-0 items-center justify-center",
                        hasContent && "mr-[8px]"
                    )}
                >
                    {icon}
                </div>
            )}
            {hasContent && (
                <div className="inline-flex min-h-[24px] items-center leading-[24px]">
                    {children}
                </div>
            )}
        </div>
    );
}
